import IconButton from '@/components/ui/custom/buttons/IconButton';
import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Checkbox from '@/components/ui/custom/dataInput/Checkbox';
import LinkText from '@/components/ui/custom/LinkText';
import useToggle from '@/data/hook/useToggle';
import i18n from '@/lib/i18n';
import { Alert, AlertTypeEnum } from '@/models/Alert';
import { UserTypeEnum } from '@/models/User';
import { accordion } from '@/utils/animations/commom';
import {
  ChatBubbleBottomCenterTextIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import NotificationTag, {
  getAlertTagsStyles,
} from '../../card/NotificationTag';
import {
  DetailTargetProps,
  NotificationActionsProps,
} from '../../card/NotificationTypes';
import useAuth from '@/data/hook/useAuth';
import { isTeacher } from '@/functions/auth';

type NotificationCardProps<T> = {
  notification: Alert;
  onMarkAsRead: (id: number, hasRead: boolean) => void;
  actor: {
    text: string;
    link?: string;
  };
  actions: React.ReactElement<NotificationActionsProps>;
  detail?: React.ReactElement<DetailTargetProps<T>>;
};

const NotificationCard = <T,>({
  notification,
  onMarkAsRead,
  actor,
  actions,
  detail,
}: NotificationCardProps<T>) => {
  const { isOpen, toggle } = useToggle();

  const { user } = useAuth();

  const { type } = notification;

  const sequence =
    notification.type === AlertTypeEnum.ABSENCE
      ? notification.extraInfo.sequence
      : undefined;

  const { t: tAlertVerbs } = useTranslation('translation', {
    keyPrefix: 'alerts.verbs',
  });

  const { t } = useTranslation('translation');

  const verb = tAlertVerbs(type, {
    count: sequence,
  });

  const isEnglish = i18n.language === 'en';

  const alertAtformattedDate = moment(notification.alertAt)
    .format(
      isEnglish ? 'MM/DD/YYYY [at] hh:mm A' : 'dddd, DD [de] MMM [às] HH:mm',
    )
    .replace(/-feira/, '');

  const underlineVerbCondition =
    type === AlertTypeEnum.ABSENCE && !!sequence && sequence > 1;

  return (
    <div
      className="p-2 border border-secondary/20 rounded-md shadow-xs w-full flex flex-col gap-1.5"
      data-testid="notificationItem"
    >
      <div
        className={twMerge(
          'w-full flex flex-col gap-1.5',
          notification.isRead ? 'opacity-60' : '',
        )}
      >
        <div className="flex justify-between items-center gap-3">
          <NotificationTag type={type} />
          <div className="flex flex-wrap justify-end text-base-content/70 text-14 gap-2">
            <Text text={alertAtformattedDate} />
            <Text text={`(${moment(notification.alertAt).fromNow()})`} />
          </div>
        </div>
        <div className="flex items-center gap-2 justify-between w-full">
          <div className="flex items-center gap-2 w-full">
            <Checkbox
              id={String(notification.id)}
              value={String(notification.isRead)}
              checked={notification.isRead}
              disabled={isTeacher(user?.userType)}
              className={{ input: 'h-5 w-5' }}
              onChange={e => onMarkAsRead(notification.id, e.target.checked)}
            />
            <div className="flex flex-wrap gap-1.5">
              <LinkText
                text={actor.text}
                className={twMerge(
                  'leading-none',
                  getAlertTagsStyles(t)[type].className.text,
                )}
                href={actor.link}
              />
              <Text
                text={verb}
                className={twMerge(
                  'font-500',
                  underlineVerbCondition ? 'underline' : '',
                )}
              />
            </div>
          </div>
          {detail && (
            <IconButton
              testId="expandButton"
              icon={
                <ChevronUpIconWithAnimation
                  className="w-5 h-5 text-primary"
                  animate={{
                    rotate: isOpen ? 180 : 0,
                  }}
                />
              }
              onClick={toggle}
            />
          )}
        </div>

        <ConditionalRenderer condition={notification.comment}>
          <div className="flex gap-1.5 items-start" data-testid="comment">
            <ChatBubbleBottomCenterTextIcon className="w-3.5 h-3.5 shrink-0" />
            <Text size="text-14" text={notification.comment} />
          </div>
        </ConditionalRenderer>
      </div>
      <ComponentGuard
        roles={[UserTypeEnum.UNIT_ADMIN, UserTypeEnum.SUPER_ADMIN]}
      >
        {actions}
      </ComponentGuard>
      <AnimatePresence>
        {isOpen && <motion.div {...accordion}>{detail}</motion.div>}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCard;

const ChevronUpIconWithAnimation = motion.create(ChevronDownIcon);
